import { ILoginInfo } from "@/models/login-info";
import { isAfter } from "date-fns";
import * as sjcl from "sjcl";
import { Vue } from "vue-property-decorator";

export default class UserAuthService {
  private static _encryptKey: string | null = null;
  private _loginInfo: ILoginInfo | null = null;

  private state = Vue.observable({
    isUserLogged: false
  });

  public static getEncryptKey() {
    if (this._encryptKey) return this._encryptKey;
    const c = sessionStorage.getItem("@1");
    if (!c) return null;
    this._encryptKey = atob(c);
    return this._encryptKey;
  }
  public static setEncryptKey(v: string) {
    this._encryptKey = v;
    sessionStorage.setItem("@1", btoa(this._encryptKey));
  }

  public getAuthHeader() {
    try {
      const loginInfo = this.userLoginInfo;
      if (!loginInfo) return null;
      if (isAfter(new Date(), new Date(loginInfo.expires))) {
        this.removeUserInfo();
        return null;
      }
      return { Authorization: "Bearer " + loginInfo.accessToken };
    } catch (e: any) {
      this.removeUserInfo();
      return null;
    }
  }

  public isUserLogged() {
    if (!this.state.isUserLogged) if (this.getAuthHeader() != null) this.state.isUserLogged = true;
    return this.state.isUserLogged;
  }

  public putUserInfo(loginInfo: ILoginInfo) {
    delete (loginInfo as any).encryptKey;
    this._loginInfo = loginInfo;
    const key = UserAuthService.getEncryptKey();
    if (!key) return;
    const encryptMsg = UserAuthService.toString(loginInfo);
    localStorage.setItem("user", UserAuthService.toString(sjcl.encrypt(key, encryptMsg)));
    this.state.isUserLogged = true;
  }

  public getUserInfo() {
    try {
      const loginInfo = this.userLoginInfo;
      if (!loginInfo) return null;
      return loginInfo;
    } catch (e: any) {
      this.removeUserInfo();
      return null;
    }
  }

  public getShipRights(shipId: number) {
    try {
      const loginInfo = this.userLoginInfo;
      if (!loginInfo) return 0;
      if (loginInfo.isAdmin) return 3;
      if (!loginInfo.userShipAccess) return 0;
      const access = loginInfo.userShipAccess.find(x => x.shipId === shipId);
      if (!access) return 0;
      return access.access;
    } catch (e: any) {
      this.removeUserInfo();
      return 0;
    }
  }

  getShipAdmin() {
    try {
      const loginInfo = this.userLoginInfo;
      if (!loginInfo) return [];
      if (!loginInfo.userShipAccess) return [];
      return loginInfo.userShipAccess.filter(x => x.access >= 4).map(x => x.shipId);
    } catch (e: any) {
      this.removeUserInfo();
      return [];
    }
  }

  isAdmin() {
    try {
      const loginInfo = this.userLoginInfo;
      if (!loginInfo) return false;
      if (!loginInfo.userShipAccess) return false;
      return loginInfo.isAdmin;
    } catch (e: any) {
      this.removeUserInfo();
      return false;
    }
  }

  public removeUserInfo() {
    localStorage.removeItem("user");
    this._loginInfo = null;
    this.state.isUserLogged = false;
  }

  private get userLoginInfo() {
    if (this._loginInfo) return this._loginInfo;
    const key = UserAuthService.getEncryptKey();
    if (!key) return null;
    const loginInfoText = localStorage.getItem("user");
    if (!loginInfoText) return null;
    const decryptMsg = UserAuthService.fromString(loginInfoText);
    if (!decryptMsg) return null;
    this._loginInfo = UserAuthService.fromString(sjcl.decrypt(key, decryptMsg)) as ILoginInfo;
    return this._loginInfo;
  }

  public static toString(object: any) {
    return btoa(JSON.stringify(object));
  }

  public static fromString(data: string) {
    return JSON.parse(atob(data));
  }
}
